import { create } from "zustand";
import { devtools } from "zustand/middleware";

/** Minimum constraint for filter types compatible with the factory. */
export type EntityFilters = {
  sort: { key?: unknown; direction: "asc" | "desc" | null };
};

export interface EntityFilterState<TFilters extends EntityFilters> {
  filters: TFilters;
  pagination: { currentPage: number };
  initialTotalCount: number | null;

  setSearchFilter: (search: string) => void;
  setFilterValue: (
    key: keyof Omit<TFilters, "sort">,
    value: TFilters[keyof Omit<TFilters, "sort">],
  ) => void;
  setSortFilter: (key?: TFilters["sort"]["key"], direction?: "asc" | "desc" | null) => void;
  clearFilters: () => void;
  setCurrentPage: (page: number) => void;
  resetToFirstPage: () => void;
  setInitialTotalCount: (count: number) => void;
  invalidateInitialTotalCount: () => void;
  reset: () => void;
}

export function createEntityFilterStore<TFilters extends EntityFilters>(
  name: string,
  initialFilters: TFilters | (() => TFilters),
) {
  /**
   * Accept either a literal or a thunk so callers with time-dependent defaults
   * (e.g. `fiscal_year: currentYear`) can re-evaluate at clear/reset time
   * instead of capturing the year at module-load time.
   */
  const getInitialFilters = (): TFilters =>
    typeof initialFilters === "function" ? (initialFilters as () => TFilters)() : initialFilters;

  return create<EntityFilterState<TFilters>>()(
    devtools(
      (set) => ({
        filters: { ...getInitialFilters() },
        pagination: { currentPage: 1 },
        initialTotalCount: null,

        setSearchFilter: (search) =>
          set((state) => ({
            filters: { ...state.filters, search },
            pagination: { currentPage: 1 },
          })),

        setFilterValue: (key, value) =>
          set((state) => ({
            filters: { ...state.filters, [key]: value },
            pagination: { currentPage: 1 },
          })),

        setSortFilter: (key, direction) =>
          set((state) => ({
            filters: {
              ...state.filters,
              sort: { key, direction: direction ?? null },
            },
            pagination: { currentPage: 1 },
          })),

        clearFilters: () =>
          set({
            filters: { ...getInitialFilters() },
            pagination: { currentPage: 1 },
          }),

        setCurrentPage: (page) => set({ pagination: { currentPage: page } }),

        resetToFirstPage: () => set({ pagination: { currentPage: 1 } }),

        setInitialTotalCount: (count) => set({ initialTotalCount: count }),

        invalidateInitialTotalCount: () => set({ initialTotalCount: null }),

        reset: () =>
          set({
            filters: { ...getInitialFilters() },
            pagination: { currentPage: 1 },
            initialTotalCount: null,
          }),
      }),
      { name },
    ),
  );
}
